import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../../services/api';
import './Login.scss';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Cornerstone Physio';
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setErrorDetails(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      // Check if login was successful - handle both success field and token presence
      if (response.data && (response.data.success || response.data.token)) {
        localStorage.setItem('adminToken', response.data.token || 'mock-jwt-token-for-development');
        // Redirect to dashboard
        navigate('/admin/dashboard');
      } else {
        setError('Login failed. Invalid response from server.');
        setErrorDetails(null);
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Check for CORS error
      if (err.isCorsError && err.corsDetails) {
        setError('CORS Error: Cannot connect to backend server');
        setErrorDetails({
          type: 'cors',
          origin: err.corsDetails.origin,
          apiUrl: err.corsDetails.apiUrl,
          troubleshooting: err.corsDetails.troubleshooting
        });
      } else if (err.response) {
        // Server responded with error
        setError(err.response.data?.error || err.response.data?.message || 'Login failed. Please check your credentials.');
        setErrorDetails(null);
      } else if (err.request) {
        // Request was made but no response received
        setError('Cannot connect to server. Please make sure the backend is running.');
        setErrorDetails({
          type: 'network',
          message: 'The backend server may not be running or is unreachable.'
        });
      } else {
        // Something else happened
        setError('Login failed. Please try again.');
        setErrorDetails(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Admin Login</h1>
          <p>Integrity Physio Admin Panel</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              <div className="error-title">{error}</div>
              {errorDetails && errorDetails.type === 'cors' && (
                <div className="error-details">
                  <div className="error-section">
                    <strong>CORS Configuration Issue</strong>
                    <p>The browser is blocking the request due to CORS (Cross-Origin Resource Sharing) policy.</p>
                  </div>
                  <div className="error-section">
                    <strong>Request Details:</strong>
                    <ul>
                      <li>Origin: <code>{errorDetails.origin}</code></li>
                      <li>API URL: <code>{errorDetails.apiUrl}</code></li>
                    </ul>
                  </div>
                  <div className="error-section">
                    <strong>Troubleshooting Steps:</strong>
                    <ol>
                      {errorDetails.troubleshooting.map((step, index) => (
                        <li key={index}>{step}</li>
                      ))}
                    </ol>
                  </div>
                  <div className="error-section">
                    <strong>Quick Fix:</strong>
                    <p>Add your origin to the backend <code>CORS_ORIGIN</code> environment variable:</p>
                    <pre className="code-block">
{`CORS_ORIGIN=${errorDetails.origin},http://localhost:3000`}
                    </pre>
                  </div>
                </div>
              )}
              {errorDetails && errorDetails.type === 'network' && (
                <div className="error-details">
                  <p>{errorDetails.message}</p>
                  <p>Please check:</p>
                  <ul>
                    <li>Backend server is running</li>
                    <li>Backend server is accessible from this network</li>
                    <li>Firewall is not blocking the connection</li>
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
            />
          </div>
          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
